// Selector-context helpers for placeholder interpolation.
// Example: ".m-${i}" should preserve selector token units like `1px`.

// Detect whether placeholder sits in a selector-like source context.
export function isSelectorPlaceholderContext(source: string, start: number, end: number): boolean {
  const lineStart = source.lastIndexOf('\n', start - 1) + 1
  const rawLineEnd = source.indexOf('\n', end)
  const lineEnd = rawLineEnd === -1 ? source.length : rawLineEnd
  const line = source.slice(lineStart, lineEnd)
  const trimmedLine = line.trim()
  const lineOffset = start - lineStart

  // Ignore placeholders in empty lines or loop header expressions.
  if (!trimmedLine || trimmedLine.startsWith('@for') || trimmedLine.startsWith('for(')) {
    // Return false for loop header context.
    return false
  } else {
    // Continue selector-context detection.
  }

  // Keep placeholders in at-rule conditions as declaration values.
  if (trimmedLine.startsWith('@')) {
    // Return false for at-rule condition context.
    return false
  } else {
    // Continue with structural checks.
  }

  const openBraceIndex = findStructuralOpenBraceIndex(line)
  // Treat tokens before a structural `{` as selector text.
  if (openBraceIndex !== -1 && lineOffset < openBraceIndex) {
    // Return true for selector portion before block open.
    return true
  } else {
    // Continue declaration-like checks.
  }

  // Regex detects declaration rows like `color: ...`.
  if (/^\s*[a-zA-Z_-][\w-]*\s*:/.test(line)) {
    // Return false for declaration-like rows.
    return false
  } else {
    // Continue selector-like heuristics.
  }

  // Keep semicolon-terminated rows in declaration context.
  if (line.includes(';')) {
    // Return false for declaration-terminated rows.
    return false
  } else {
    // Continue selector-leading token checks.
  }

  // Regex matches common selector-leading tokens.
  if (/^\s*([.#\[:&*>+~]|[a-zA-Z][\w-]*(?=[\s.#\[:&*>+~]|$))/.test(line)) {
    // Return true when row starts like a selector.
    return true
  } else {
    // Continue interpolation suffix checks.
  }

  const beforePlaceholder = line.slice(0, lineOffset).trimEnd()
  const previousChar = beforePlaceholder[beforePlaceholder.length - 1] ?? ''
  // Treat suffix interpolations like `.m-${i}` as selector placeholders.
  if (previousChar === '-' || previousChar === '_' || previousChar === '.') {
    // Return true for selector token suffix interpolation.
    return true
  } else {
    // Return false for non-selector contexts.
    return false
  }
}

// Find block-opening `{` while ignoring interpolation `${...}` braces.
export function findStructuralOpenBraceIndex(line: string): number {
  // Scan line chars and skip placeholder braces that start with `$`.
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    // Return first real block brace used by selector/at-rule syntax.
    if (char === '{' && line[index - 1] !== '$') {
      // Return structural open brace index.
      return index
    } else {
      // Continue brace search.
    }
  }

  // Return -1 when structural brace was not found.
  return -1
}

// Normalize selector placeholder value by stripping only wrapping quotes.
export function normalizeSelectorLoopValue(value: string): string {
  // Return the selector token with wrapping quotes removed.
  return stripWrappingQuotes(value.trim())
}

// Remove matching single/double quotes around one full token.
export function stripWrappingQuotes(value: string): string {
  // Keep short tokens unchanged when they cannot be quoted pairs.
  if (value.length < 2) {
    // Return original value for short tokens.
    return value
  } else {
    // Continue quote pair validation.
  }

  const first = value[0]
  const last = value[value.length - 1]
  // Strip only balanced `'...'` or `"..."` wrappers.
  if ((first === "'" && last === "'") || (first === '"' && last === '"')) {
    // Return unwrapped token.
    return value.slice(1, -1)
  } else {
    // Return untouched token when quotes are unbalanced.
    return value
  }
}
